/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {map, Observable, of} from 'rxjs';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface AdUnitModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface AdUnitLookupModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface AdUnitSearchResult {
  id: string;
  name?: string;
  [key: string]: any;
}

export interface AdUnitTimeZone {
  zone: string;
  offset: string;
}

@Injectable({providedIn: 'root'})
export class AdUnitApiService extends SignalAdminResourceApiBase<AdUnitModel> {
  constructor(client: AdnApiClientService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'adunits', {}, () => localUserProfile?.isReadOnly() || false);
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<AdUnitModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((item) => this.normalize(item))
    })));
  }

  override get(id: string): Observable<AdUnitModel> {
    return super.get(id).pipe(map((model) => this.normalize(model)));
  }

  override save(model: AdUnitModel): Observable<AdUnitModel> {
    const submitted = {...model};
    const amount = submitted.floorPrice?.amount;
    if (Object.prototype.hasOwnProperty.call(submitted, 'floorPrice') && !amount && amount !== 0) {
      submitted.floorPrice = null;
    }
    return super.save(submitted).pipe(map((saved) => this.normalize(saved)));
  }

  getMany(ids: string[]): Observable<SignalAdminResourcePage<AdUnitModel>> {
    if (!ids.length) {
      return of({results: [], totalCount: 0, page: 0});
    }
    return this.client.request<SignalAdminResourcePage<AdUnitModel>>({
      method: 'GET',
      url: this.resourceUri('adunits'),
      params: {
        id: ids.join(';'),
        pageSize: ids.length,
        includeActive: true,
        includeInactive: true,
        includeHidden: true,
        orderBy: 'name'
      }
    }).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((item) => this.normalize(item))
    })));
  }

  listBlocklists(): Observable<SignalAdminResourcePage<AdUnitLookupModel>> {
    return this.listLookup('blocklists', true);
  }

  listRulesets(): Observable<SignalAdminResourcePage<AdUnitLookupModel>> {
    return this.listLookup('rulesets', false);
  }

  listTimeZones(): Observable<AdUnitTimeZone[]> {
    return this.client.request<{results?: AdUnitTimeZone[]}>({
      method: 'GET',
      url: this.resourceUri('timezones'),
      params: {context: null}
    }).pipe(map((response) => response.results || []));
  }

  searchSites(query: string): Observable<AdUnitSearchResult[]> {
    return this.search(query, 'Site', 50);
  }

  searchLineItems(query: string): Observable<AdUnitSearchResult[]> {
    return this.search(query, 'LineItem', 10);
  }

  getTags(id: string): Observable<any> {
    return this.client.request<any>({
      method: 'GET',
      url: this.resourceUri(['adunittags', id]),
      params: {}
    });
  }

  getSite(id: string): Observable<AdUnitLookupModel> {
    return this.getRelated('sites', id);
  }

  getRuleset(id: string): Observable<AdUnitLookupModel> {
    return this.getRelated('rulesets', id);
  }

  getEarningsAccount(id: string): Observable<AdUnitLookupModel> {
    return this.getRelated('earningsaccounts', id);
  }

  getLineItems(ids: string[]): Observable<SignalAdminResourcePage<AdUnitLookupModel>> {
    return this.client.request<SignalAdminResourcePage<AdUnitLookupModel>>({
      method: 'GET',
      url: this.resourceUri('lineitems'),
      params: {id: ids.join(';'), showBudgetSpend: true}
    });
  }

  getCreativeInfo(ids: string[]): Observable<SignalAdminResourcePage<any>> {
    return this.client.request<SignalAdminResourcePage<any>>({
      method: 'GET',
      url: this.resourceUri(['creatives', 'info']),
      params: {id: ids.join(';'), minimal: true}
    });
  }

  private listLookup(path: string, minimal: boolean): Observable<SignalAdminResourcePage<AdUnitLookupModel>> {
    return this.client.request<SignalAdminResourcePage<AdUnitLookupModel>>({
      method: 'GET',
      url: this.resourceUri(path),
      params: {
        includeActive: true,
        includeInactive: false,
        includeHidden: false,
        orderBy: 'name',
        pageSize: 1001,
        minimal
      }
    });
  }

  private search(query: string, types: string, pageSize: number): Observable<AdUnitSearchResult[]> {
    const normalizedQuery = (query || '').trim();
    const searchQuery = normalizedQuery && normalizedQuery.length < 3 && !normalizedQuery.endsWith('*')
      ? normalizedQuery + '*'
      : normalizedQuery;
    const params: Record<string, any> = {q: searchQuery, types, pageSize, objectState: 'ACTIVE'};
    if (!searchQuery) {
      params.orderBy = 'NAME';
    }
    return this.client.request<{searchResults?: AdUnitSearchResult[]}>({
      method: 'GET',
      url: this.resourceUri('search'),
      params
    }).pipe(map((response) => response.searchResults || []));
  }

  private getRelated(path: string, id: string): Observable<AdUnitLookupModel> {
    return this.client.request<AdUnitLookupModel>({
      method: 'GET',
      url: this.resourceUri([path, id]),
      params: {}
    });
  }

  private normalize(model: AdUnitModel): AdUnitModel {
    return {...model, floorPrice: model.floorPrice || {}};
  }
}
